import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { WHATSAPP_URL } from "@/lib/whatsapp";

const ServicePricing = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [shouldLoad]);

  return (
    <section
      id="inversion"
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0a162a 0%, #1a2d4c 100%)" }}
    >
      <div className="container px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-10"
        >
          <span className="text-[#c7d2fe] text-sm font-semibold tracking-widest uppercase mb-4 block">
            AGENDÁ TU LLAMADA
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">
            Reservá tu <span className="text-[#c7d2fe] italic">Llamada Estratégica</span>
          </h2>
          <p className="text-white/50 text-sm mt-2">
            *Sólo para marcas con amplia trayectoria que estén dispuestas a invertir en su crecimiento.
          </p>
        </motion.div>

        {/* Calendly Embed */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-5xl mx-auto"
        >
          {shouldLoad ? (
            <div
              className="calendly-inline-widget rounded-2xl overflow-hidden border border-[#5b7cab]/30"
              data-url="https://calendly.com/asesor-coressenglobal/llamada?hide_gdpr_banner=1"
              style={{ minWidth: "320px", height: "700px" }}
            />
          ) : (
            <div
              className="rounded-2xl overflow-hidden border border-[#5b7cab]/30 bg-[#1a2d4c]/50 flex items-center justify-center"
              style={{ minWidth: "320px", height: "700px" }}
            >
              <div className="text-center text-white/40">
                <div className="w-8 h-8 border-2 border-[#7ab7fd] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p>Cargando calendario...</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* WhatsApp fallback */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-8 max-w-2xl mx-auto"
        >
          <p className="text-white/50 text-sm mb-3">
            ¿No encontrás lugar? Tocá en el botón para hablar con un asesor de nuestro equipo y asegurar tu llamada.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#25D366] text-white font-bold text-sm tracking-wider rounded-full hover:bg-[#20bd5a] transition-all duration-300 hover:scale-105"
          >
            HABLAR CON UN ASESOR
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicePricing;
